import React, { createContext, useContext } from 'react'

import { getTheme, ThemeProvider } from '@octane/spark'
import { getBrandConfig } from 'branding'
import { setBrandIdentifier } from 'branding/branding.store'
import fallbackConfig from 'branding/brands/fallback'
import { brandConfig as lenderBrandConfig } from 'branding/lender'
import { BrandConfig } from 'branding/types'
import LoadingSpinner from 'components/LoadingSpinner'
import { defaultsDeep as _defaultsDeep } from 'lodash'
import LenderName from 'state/enums/LenderName'
import useSwr from 'swr'

import { useLender } from './LenderContextProvider'

interface BrandConfigContextProviderProps {
    readonly children: React.ReactNode
}

export const BrandConfigContext = createContext<BrandConfig>({} as BrandConfig)

export function BrandConfigContextProvider({
    children,
}: BrandConfigContextProviderProps) {
    const { name: lenderName } = useLender()

    const { data: brandConfig, isLoading } = useSwr(
        'brandConfig',
        getBrandConfig,
        {
            errorRetryCount: 0,
            refreshInterval: 0,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    )

    const value = _defaultsDeep(
        {},
        lenderBrandConfig[lenderName as LenderName] ?? {},
        brandConfig ?? {},
        fallbackConfig,
    )

    const { identifier } = value

    React.useEffect(() => {
        if (brandConfig != null) {
            setBrandIdentifier(identifier)
        }
    }, [brandConfig, identifier])

    if (isLoading) {
        return (
            <ThemeProvider theme={getTheme(fallbackConfig.sparkThemeId)}>
                <LoadingSpinner />
            </ThemeProvider>
        )
    }

    return (
        <BrandConfigContext.Provider value={value}>
            {children}
        </BrandConfigContext.Provider>
    )
}

export const useBrandConfig = (): BrandConfig => {
    return useContext(BrandConfigContext)
}
